import React, { useState } from 'react';

import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { Tooltip } from '../Tooltip/Tooltip';

import { IModelSelectorProps } from './ModelSelector.type';

export const ModelSelector: React.FC<IModelSelectorProps> = ({
  model,
  onChange,
  disabled = false,
  options,
}) => {
  const [open, setOpen] = useState(false);

  const selected = options.find(o => o.value === model) ?? options[0];

  const dropdownContent = (onClose: () => void) => (
    <div
      className="bg-bg-primary overflow-hidden"
      role="listbox"
      aria-label="Model Quality"
    >
      <div className="flex items-center justify-between px-bg-v2-md py-bg-v2-md">
        <Text variant="text-xs" weight="semibold" className="text-text-quaternary tracking-widest uppercase">
          Model Selection
        </Text>
      </div>

      <div className="flex-col gap-bg-v2-xxs">
        {options.map((option) => {
          const isSelected = model === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={isSelected}
              onClick={() => {
                onChange(option.value);
                onClose();
                setOpen(false);
              }}
              className={`
                w-full text-left rounded-bg-v2-lg px-bg-v2-md py-bg-v2-md
                flex items-center gap-bg-v2-md
                transition-all duration-150 group
                ${isSelected
                  ? 'bg-bg-brand-primary'
                  : 'hover:bg-bg-secondary'
                }
              `}
            >
              <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                <Text
                  variant="text-sm"
                  weight={isSelected ? 'semibold' : 'medium'}
                  className={isSelected ? 'text-text-brand-primary' : 'text-text-primary'}
                >
                  {option.label}
                </Text>
                <Text variant="text-xs" className={`leading-snug ${isSelected ? 'text-text-brand-secondary' : 'text-text-tertiary'}`}>
                  {option.description}
                </Text>
              </span>

              <span className={`flex-shrink-0 transition-opacity duration-150 ${isSelected ? 'opacity-100' : 'opacity-0'}`}>
                <Icon name={IconName.Check} size="xs" className="text-text-brand-primary" inheritColor />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <Tooltip
      content={dropdownContent}
      position="top"
      interactive
      trigger="contextmenu"
      delay={0}
      onVisibleChange={setOpen}
    >
      <button
        type="button"
        disabled={disabled}
        className={`
          flex items-center gap-bg-v2-sm
          px-bg-v2-lg py-bg-v2-md
          rounded-bg-v2-full
          border border-border-secondary w-full
          bg-bg-secondary
          transition-all duration-150
          ${disabled
            ? 'opacity-50 cursor-not-allowed'
            : open
              ? 'bg-bg-primary border-border-brand text-text-brand-primary'
              : 'hover:bg-bg-primary-hover hover:border-border-primary cursor-pointer'
          }
        `}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Select model quality"
      >
        <Text
          variant="text-sm"
          weight="medium"
          className={`transition-colors duration-150 ${open ? 'text-text-brand-primary' : 'text-text-secondary'}`}
        >
          {selected.label}
        </Text>
        <Icon
          name={IconName.ChevronDown}
          size="sm"
          className={`transition-all duration-200 ${open ? 'text-text-brand-primary rotate-180' : 'text-text-quaternary'}`}
          inheritColor
        />
      </button>
    </Tooltip>
  );
};
